import {
  useChoices,
  useConfigMapJson,
  useConsumerProfile,
  useCreateDevice,
  useGenerateMeterNumber,
  useGetManufacturers,
  useGetMeterModel,
  useMeterDetail,
  useMeterSearchPrompt,
  usePatchDeviceManagement,
  useUtilityServices,
} from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { convertToSnakeCase } from "@shared/utils/caseConverter";
import { toast } from "@shared/ui/use-toast";
import { logEvent } from "@shared/analytics/analytics";
import { AlertCircle, ArrowLeft, CheckCircle, UserCheck } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CustomTooltip } from "@shared/components/CustomTooltip";

// Types for better type safety
interface FieldState {
  subCategory: string | null;
  premise: string | null;
  utility: string | null;
  manufacturer: string | null;
  consumerSearch: string;
}

interface DropdownOptions {
  meterTypes: Array<{ value: string | number; label: string }>;
  categoryConfig: Array<{ value: string | number; label: string }>;
  subCategoryConfig: Array<{ value: string | number; label: string }>;
  territoryConfig: Array<{ value: string | number; label: string }>;
  consumersList: Array<{ value: string | number; label: string }>;
}

const AddEditMeter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id) && id !== "new";
  const remoteUtilityId = useRemoteUtilityId();

  // ===============================
  // STATE MANAGEMENT
  // ===============================
  const [fieldState, setFieldState] = useState<FieldState>({
    subCategory: null,
    premise: null,
    utility: null,
    manufacturer: null,
    consumerSearch: "",
  });

  const [assignmentStatus, setAssignmentStatus] = useState<boolean>(false);
  const [meterType, setMeterType] = useState<string>("");
  const [meterManufacturer, setMeterManufacturer] = useState<string>("");
  const [displayMeterNo, setDisplayMeterNo] = useState<string>("");
  const [searchConsumer, setSearchConsumer] = useState<string>("");
  const [isEditDataLoaded, setIsEditDataLoaded] = useState<boolean>(false);
  const { toast } = useToast();

  // ===============================
  // API HOOKS
  // ===============================
  const { data: meterNo, isLoading: meterNoLoading } =
    useGenerateMeterNumber(remoteUtilityId);
  const { data: utilityServices } = useUtilityServices(remoteUtilityId);
  const { mutate: createDevice } = useCreateDevice();
  const { data: manufacturers } = useGetManufacturers();
  const { data: meterTypes } = useChoices({ choice: "meter_type" });

  // Get edit data first to extract required parameters
  const { data: editData } = useMeterDetail(Number(id), remoteUtilityId);
  console.log("Edit Data of meter:", editData);

  // Config data hooks - Initialize with edit data if available
  const { data: categoryConfig } = useConfigMapJson(
    "category_json",
    remoteUtilityId,
    "category"
  );
  const { data: territoryConfig } = useConfigMapJson(
    "territory_json",
    remoteUtilityId,
    "premise"
  );
  const { data: subCategoryConfig } = useConfigMapJson(
    "category_json",
    remoteUtilityId,
    "sub_category",
    fieldState.subCategory || (isEditing && editData?.category) || null
  );

  // Consumer and meter data hooks - Initialize with edit data if available
  const { data: consumersList } = useMeterSearchPrompt({
    remoteUtilityId,
    searchData: searchConsumer || (isEditing && editData?.consumerNo) || "",
    productCode:
      fieldState.utility || (isEditing && editData?.productCode) || "",
    premise: fieldState.premise || (isEditing && editData?.premise) || "",
  });

  const { data: meterModels } = useGetMeterModel(
    meterType || (isEditing && editData?.meterType?.toString()) || "",
    meterManufacturer || (isEditing && editData?.meterMake?.toString()) || ""
  );

  const { data: profile } = useConsumerProfile(
    remoteUtilityId,
    fieldState.consumerSearch || (isEditing && editData?.consumerNo) || ""
  );

  const patchDeviceMutation = usePatchDeviceManagement(Number(id));

  // ===============================
  // MEMOIZED OPTIONS
  // ===============================
  const utilityOptions = useMemo(() => {
    if (!utilityServices || !Array.isArray(utilityServices)) return [];
    return utilityServices
      .filter((utility) => utility.isActive === true)
      .map((utility) => ({
        value: utility.name,
        label: utility.name,
      }));
  }, [utilityServices]);

  const manufacturerOptions = useMemo(() => {
    if (!manufacturers || !Array.isArray(manufacturers)) return [];
    return manufacturers.map((model) => ({
      value: model.id,
      label: model.name,
    }));
  }, [manufacturers]);

  const meterModelOptions = useMemo(() => {
    if (!meterModels || !Array.isArray(meterModels)) return [];
    return meterModels.map((model) => ({
      value: model.id,
      label: model.meterModel,
    }));
  }, [meterModels]);

  const dropdownOptions: DropdownOptions = useMemo(() => {
    let consumerOptions = mapToLabelValue(consumersList, "value", "value");

    // Add existing consumer to options when editing
    if (isEditing && editData?.consumerNo) {
      const existingConsumer = consumerOptions.find(
        (option) => option.value === editData.consumerNo
      );
      if (!existingConsumer) {
        consumerOptions = [
          { value: editData.consumerNo, label: editData.consumerNo },
          ...consumerOptions,
        ];
      }
    }

    return {
      meterTypes: mapToLabelValue(meterTypes),
      categoryConfig: mapToLabelValue(categoryConfig, "name", "code"),
      subCategoryConfig: mapToLabelValue(subCategoryConfig, "name", "code"),
      territoryConfig: mapToLabelValue(territoryConfig, "name", "code"),
      consumersList: consumerOptions,
    };
  }, [
    meterTypes,
    categoryConfig,
    subCategoryConfig,
    territoryConfig,
    consumersList,
    isEditing,
    editData,
  ]);

  const fields: FormField[] = [
    // Basic Information Section
    {
      group: "",
      groupOrder: 1,
      name: "device_no",
      type: "text",
      label: "Device Number",
      required: true,
      disabled: !!isEditing,
      placeholder: "Enter meter device number",
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      helperText: "Unique identifier for the meter device",
      className: "col-span-2",
    },
    {
      name: "product_code",
      type: "select",
      label: "Utility Service",
      required: true,
      disabled: !!isEditing,
      placeholder: "Select utility service",
      options: utilityOptions,
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      clearable: true,
      searchable: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Choose the utility service assigned to this meter",
    },
    {
      name: "meter_type",
      type: "select",
      label: "Meter Type",
      placeholder: "Select meter type",
      disabled: !!isEditing,
      required: true,
      options: dropdownOptions?.meterTypes,
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Select the type of the meter (e.g., digital, analog)",
    },
    {
      name: "category",
      type: "select",
      label: "Category",
      required: true,
      placeholder: "Select category",
      options: dropdownOptions?.categoryConfig,
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      clearable: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Choose the consumer category for billing",
    },
    {
      name: "sub_category",
      type: "select",
      label: "Sub Category",
      required: true,
      placeholder: "Select sub-category",
      options: dropdownOptions?.subCategoryConfig,
      dependsOn: "category",
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      clearable: true,
      helperText: "Select category first to enable sub-category options",
      groupOrder: 1,
      className: "col-span-2",
    },
    {
      name: "premise",
      type: "select",
      label: "Premise",
      required: true,
      placeholder: "Select premise",
      options: dropdownOptions.territoryConfig,
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      clearable: true,
      groupOrder: 1,
      searchable: true,
      className: "col-span-2",
      helperText: "Premise where the meter will be installed",
    },
    {
      name: "floor",
      type: "text",
      label: "Floor",
      placeholder: "Enter floor number/name",
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      maxLength: 50,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Specify floor location of the meter",
    },
    {
      name: "lat",
      type: "negative-decimal",
      label: "Latitude",
      placeholder: "e.g., 40.7128",
      size: "medium",
      variant: "outlined",
      required: false,
      fullWidth: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Latitude coordinate of meter location",
    },
    {
      name: "long",
      type: "negative-decimal",
      label: "Longitude",
      placeholder: "e.g., -74.0060",
      required: false,
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Longitude coordinate of meter location",
    },
    {
      name: "installed_on",
      type: "datepicker",
      label: "Installation Date",
      required: true,
      disabled: !!isEditing,
      defaultValue: "08-06-2025",
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      helperText: "Date when the meter was installed",
      groupOrder: 1,
      className: "col-span-2",
    },

    // Consumer Assignment Section
    {
      group: "",
      name: "is_assigned",
      disabled: isEditing,
      type: "switch",
      label: "Consumer Assignment",
      defaultValue: assignmentStatus,
      size: "medium",
      className: "col-span-4",
      helperText: "Toggle on to Assign Consumer, off to Unassign Consumer",
      groupOrder: 2,
    },
    {
      name: "consumer_no",
      type: "multi-select",
      onSearchChange: (term: string) => setSearchConsumer(term),
      maxSelection: 1,
      label: "Select Consumer",
      placeholder: "Select consumer",
      options: dropdownOptions.consumersList,
      dependsOn: "is_assigned",
      showWhen: (formValues: any) => formValues.is_assigned === true,
      disabled: !!editData?.consumerNo && isEditing,
      size: "medium",
      variant: "outlined",
      required: !isEditing && assignmentStatus,
      fullWidth: true,
      clearable: true,
      searchable: true,
      className: "col-span-4",
      groupOrder: 2,
      helperText: "Assign a consumer to this meter",
    },

    // Consumer Information Display
    {
      groupOrder: 3,
      name: "consumer_name",
      type: "autoPopulated",
      label: "Consumer Name",
      value: profile?.firstName + " " + profile?.lastName,
      dependsOn: "consumer_no",
      showWhen: (formValue: any) =>
        formValue.is_assigned &&
        formValue.consumer_no &&
        Array.isArray(formValue.consumer_no) &&
        formValue.consumer_no.length > 0 &&
        !!searchConsumer &&
        !!profile,
      className: "col-span-1",
      helperText: "Name of the assigned consumer",
    },
    {
      groupOrder: 3,
      name: "consumer_premise",
      type: "autoPopulated",
      label: "Premise",
      value: profile?.territoryData?.service?.premise,
      dependsOn: "consumer_no",
      showWhen: (formValue: any) =>
        formValue.is_assigned &&
        formValue.consumer_no &&
        Array.isArray(formValue.consumer_no) &&
        formValue.consumer_no.length > 0 &&
        !!searchConsumer &&
        !!profile,
      className: "col-span-1",
      helperText: "Premise linked to the assigned consumer",
    },
    {
      groupOrder: 3,
      name: "consumer_status",
      type: "autoPopulated",
      label: "Consumer Status",
      value: profile?.statusDisplay,
      dependsOn: "consumer_no",
      showWhen: (formValue: any) =>
        formValue.is_assigned &&
        formValue.consumer_no &&
        Array.isArray(formValue.consumer_no) &&
        formValue.consumer_no.length > 0 &&
        !!searchConsumer &&
        !!profile,
      className: "col-span-1",
      helperText: "Status of the assigned consumer",
    },
    {
      groupOrder: 3,
      name: "consumer_utility_service",
      type: "autoPopulated",
      label: "Utility Service",
      value: profile?.consumerMappingData?.[0]?.utilityService,
      dependsOn: "consumer_no",
      showWhen: (formValue: any) =>
        formValue.is_assigned &&
        formValue.consumer_no &&
        Array.isArray(formValue.consumer_no) &&
        formValue.consumer_no.length > 0 &&
        !!searchConsumer &&
        !!profile,
      className: "col-span-1",
      helperText: "Utility service linked to the assigned consumer",
    },

    // Reading Information Section
    {
      group: "",
      name: "current_reading",
      type: "number",
      label: "First Reading",
      placeholder: "Enter initial reading value",
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      disabled: !!editData?.currentReading,
      required: assignmentStatus,
      dependsOn: "is_assigned",
      showWhen: (formValues: any) => formValues.is_assigned === true,
      helperText: "Initial meter reading value",
      groupOrder: 4,
      className: "col-span-2",
    },
    {
      name: "current_reading_date",
      type: "datepicker",
      label: "First Reading Date",
      defaultValue: "08-06-2025",
      size: "medium",
      variant: "outlined",
      required: assignmentStatus,
      fullWidth: true,
      disabled: !!editData?.currentReadingDate,
      dependsOn: "is_assigned",
      showWhen: (formValues: any) => formValues.is_assigned === true,
      helperText: "Date of the first reading",
      groupOrder: 4,
      className: "col-span-2",
    },

    // Manufacturer Details Section
    {
      name: "meter_make",
      type: "select",
      label: "Manufacturer",
      placeholder: "Select manufacturer",
      options: manufacturerOptions,
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      clearable: true,
      searchable: true,
      groupOrder: 4,
      className: "col-span-2",
      helperText: "Brand or make of the meter",
    },
    {
      name: "meter_model",
      type: "select",
      label: "Model",
      placeholder: "Select manufacturer first",
      options: meterModelOptions,
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      clearable: true,
      searchable: true,
      helperText: "Select manufacturer first to enable model options",
      groupOrder: 4,
      className: "col-span-2",
    },
  ];

  const formConfig: FormConfig = {
    fields: fields,
    shouldUnregister: true,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      radio: "",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };

  const meterForm = FormService.createForm(formConfig);

  // ===============================
  // VALIDATION FUNCTIONS
  // ===============================
  const validateConsumerApproval = (
    isAssigned: boolean,
    consumerStatus?: string
  ): boolean => {
    if (!isAssigned) {
      return true;
    }

    // If consumer is assigned, check if status is APPROVED
    if (isAssigned && consumerStatus !== "APPROVED") {
      toast({
        title: "Error",
        description:
          "Consumer status is not approved. Please select an approved consumer.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  // ===============================
  // EVENT HANDLERS
  // ===============================
  const handleFieldChange = (field: string, data: any) => {
    if (field === "is_assigned") {
      setAssignmentStatus(data);
      if (!data) {
        setSearchConsumer("");
        setFieldState((prev) => ({
          ...prev,
          consumerSearch: "",
          current_reading: "",
          current_reading_date: "",
        }));
      }
      return;
    }

    if (field === "consumer_no") {
      if (!data || (Array.isArray(data) && data.length === 0)) {
        setSearchConsumer("");
        setFieldState((prev) => ({ ...prev, consumerSearch: "" }));
      } else if (Array.isArray(data) && data.length > 0) {
        setSearchConsumer(data[0]);
        setFieldState((prev) => ({ ...prev, consumerSearch: data[0] }));
      }
      return;
    }

    // Handle meter type changes
    if (field === "meter_type") {
      setMeterType(data);
      return;
    }

    // Handle manufacturer changes
    if (field === "meter_make") {
      setMeterManufacturer(data);
      return;
    }

    // Handle dependent field updates
    const fieldUpdateMap: { [key: string]: (data: any) => void } = {
      category: (data) =>
        setFieldState((prev) => ({ ...prev, subCategory: data })),
      product_code: (data) =>
        setFieldState((prev) => ({ ...prev, utility: data })),
      premise: (data) => setFieldState((prev) => ({ ...prev, premise: data })),
      current_reading: (data) =>
        setFieldState((prev) => ({ ...prev, current_reading: data })),
      current_reading_date: (data) =>
        setFieldState((prev) => ({ ...prev, current_reading_date: data })),
    };

    const updateHandler = fieldUpdateMap[field];
    if (updateHandler) {
      updateHandler(data);
    }
  };
  const handleCancel = () => {
    navigate(-1);
  };

  // ===============================
  // UTILITY FUNCTIONS
  // ===============================

  const cleanPayload = (data: any) => {
    const cleaned: any = {};

    Object.keys(data).forEach((key) => {
      const value = data[key];
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isMeaningful =
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== "null" &&
        value !== "undefined" &&
        !isEmptyArray;

      if (isMeaningful) {
        if (
          key === "consumerNo" ||
          (key === "consumer_no" && Array.isArray(value) && value.length === 1)
        ) {
          cleaned[key] = value[0];
        } else {
          cleaned[key] = value;
        }
      }
    });

    return cleaned;
  };

  const prepareFormData = (data: any) => {
    if (data.meterMake && manufacturers) {
      const selectedManufacturer = manufacturers.find(
        (m: any) => m.id === data.meterMake
      );
      console.log("selectedManufacturer", selectedManufacturer);
      if (selectedManufacturer) {
        data.meterMake = selectedManufacturer.id;
      }
    }

    return data;
  };

  const handleSubmitForm = async (data: any) => {
    try {
      if (!meterNo?.meterNumber && !isEditing) {
        toast({
          title: "Error",
          description:
            "Meter number is not generated yet. Please wait and try again.",
          variant: "destructive",
        });

        return;
      }

      data = prepareFormData(data);
      let formData = FormService.createPayload(data, fields);
      formData = cleanPayload(formData);

      if (isEditing) {
        const convertedEditData = convertToSnakeCase(editData);
        let editPayload = FormService.getUpdatedFields(convertedEditData, data);
        editPayload = cleanPayload(editPayload);
        if (editData?.consumerNo) {
          delete editPayload.is_assigned;
        }

        if (data.is_assigned === false) {
          delete editPayload.installed_on;
          delete editPayload.current_reading;
          delete editPayload.current_reading_date;
          editPayload.status = 0;
          editPayload.is_assigned = false;
          if (editData?.consumerNo) {
            editPayload.consumer_no = editData.consumerNo;
          }
        } else {
          editPayload;
        }
        patchDeviceMutation.mutate(
          { deviceId: id, payload: editPayload },
          {
            onSuccess: () => {
              navigate(`/mx/device-management/meter/view/${id}?tab=readings`);
            },
            onError: (error) => {
              console.error("Edit error:", error);
              toast({
                title: "Error",
                description: "Failed to update meter. Please try again.",
                variant: "destructive",
              });
            },
          }
        );
      } else {
        if (
          !validateConsumerApproval(data.is_assigned, profile?.statusDisplay)
        ) {
          return;
        }
        const requiredFields = {
          meter_number: meterNo?.meterNumber || "",
          remote_utility_id: remoteUtilityId,
          status: assignmentStatus ? 1 : 0,
          physical_status: 0,
        };

        Object.assign(formData, requiredFields);

        if (assignmentStatus) {
          formData.reading_status = 0;
        }

        if (!formData.meter_number) {
          toast({
            title: "Error",
            description: "Unable to generate meter number. Please try again.",
            variant: "destructive",
          });
          return;
        }
        console.log("debug addd", formData);
        await createDevice(formData, {
          onSuccess: () => {
            logEvent("New Meter added", {
              ...formData,
            });
          },
          onError: () => {
            logEvent("New Meter Addition Failed", {
              ...formData,
            });
          },
        });
        toast({
          title: "Success",
          description: "Meter created successfully",
          variant: "default",
        });
        navigate("/mx/device-management/meter", { replace: true });
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast({
        title: "Error",
        description: "An error occurred while saving the meter",
        variant: "destructive",
      });
    }
  };
  const handleNextSubmit = async (data: any) => {
    try {
      // Validate consumer approval status before proceeding
      if (!validateConsumerApproval(data.is_assigned, profile?.statusDisplay)) {
        return;
      }

      if (!meterNo?.meterNumber) {
        toast({
          title: "Error",
          description:
            "Meter number is not generated yet. Please wait and try again.",
          variant: "destructive",
        });

        return;
      }

      data = prepareFormData(data);
      let formData = FormService.createPayload(data, fields);
      formData = cleanPayload(formData);
      delete formData.is_assigned;

      const requiredFields = {
        meter_number: meterNo?.meterNumber,
        remote_utility_id: remoteUtilityId,
        status: assignmentStatus ? 1 : 0,
        physical_status: 0,
      };

      Object.assign(formData, requiredFields);

      if (assignmentStatus) {
        formData.reading_status = 0;
      }

      await createDevice(formData, {
        onSuccess: () => {
          logEvent("New Meter added", {
            ...formData,
          });
          toast({
            title: "default",
            description: "Meter added successfully",
          });
        },
        onError: () => {
          logEvent("New Meter Addition Failed", {
            ...formData,
          });
        },
      });
      FormService.resetForm(meterForm, fields);
      setAssignmentStatus(false);
      setSearchConsumer("");
      setFieldState({
        subCategory: null,
        premise: null,
        utility: null,
        manufacturer: null,
        consumerSearch: "",
      });
    } catch (error) {
      console.error("Next submit error:", error);
      toast({
        title: "Error",
        description: "An error occurred while saving the meter",
        variant: "destructive",
      });
    }
  };

  // ===============================
  // EFFECTS FOR DATA LOADING
  // ===============================

  // Initialize field states early when edit data is available
  useEffect(() => {
    if (isEditing && editData && Object.keys(editData).length > 0) {
      // Set field states for dependent dropdowns immediately
      setFieldState((prev) => ({
        ...prev,
        consumerSearch: editData.consumerNo || "",
        utility: editData.productCode || null,
        premise: editData.premise || null,
        subCategory: editData.category || null,
      }));

      // Set other states immediately
      if (editData.meterType) {
        setMeterType(editData.meterType.toString());
      }

      if (editData.meterMake) {
        setMeterManufacturer(editData.meterMake.toString());
      }

      if (editData.consumerNo) {
        setAssignmentStatus(true);
        setSearchConsumer(editData.consumerNo);
      }

      setDisplayMeterNo(editData.meterNumber);
    }
  }, [isEditing, editData]);

  // Populate form data when all dependencies are loaded
  useEffect(() => {
    if (
      isEditing &&
      editData &&
      Object.keys(editData).length > 0 &&
      !isEditDataLoaded &&
      // Wait for essential dropdown data to be loaded
      categoryConfig &&
      territoryConfig &&
      manufacturers &&
      meterTypes
    ) {
      const convertedEditData = convertToSnakeCase(editData);
      console.log("Converted Edit Data:", convertedEditData);

      // Populate form with edit data
      FormService.populateForm(meterForm, convertedEditData, fields);

      // Set form values for dependent fields
      if (editData.category) {
        meterForm.setValue("category", editData.category);
      }
      if (editData.productCode) {
        meterForm.setValue("product_code", editData.productCode);
      }
      if (editData.premise) {
        meterForm.setValue("premise", editData.premise);
      }
      if (editData.meterType) {
        meterForm.setValue("meter_type", editData.meterType);
      }

      // Handle manufacturer selection
      if (editData.meterMake && manufacturers) {
        const selectedManufacturer = manufacturers.find(
          (m: any) =>
            m.name === editData.meterMake || m.id === editData.meterMake
        );

        if (selectedManufacturer) {
          meterForm.setValue("meter_make", selectedManufacturer.id);
        }
      }

      // Handle consumer assignment
      if (editData.consumerNo) {
        meterForm.setValue("is_assigned", true);
        meterForm.setValue("consumer_no", [editData.consumerNo]);
      }

      // Mark edit data as loaded
      setIsEditDataLoaded(true);
    }
  }, [
    isEditing,
    editData,
    manufacturers,
    meterForm,
    isEditDataLoaded,
    categoryConfig,
    territoryConfig,
    meterTypes,
    fields,
  ]);

  // Handle sub-category population when sub-category config is loaded
  useEffect(() => {
    if (
      isEditing &&
      editData &&
      isEditDataLoaded &&
      editData.subCategory &&
      subCategoryConfig &&
      subCategoryConfig.length > 0
    ) {
      meterForm.setValue("sub_category", editData.subCategory);
    }
  }, [isEditing, editData, isEditDataLoaded, subCategoryConfig, meterForm]);

  // Handle meter model population when meter models are loaded
  useEffect(() => {
    if (
      isEditing &&
      editData &&
      isEditDataLoaded &&
      editData.meterModel &&
      meterModels &&
      meterModels.length > 0
    ) {
      meterForm.setValue("meter_model", editData.meterModel);
    }
  }, [isEditing, editData, isEditDataLoaded, meterModels, meterForm]);

  const renderMeterStatusBadges = () => (
    <div className="bg-muted/20 p-4 rounded-lg border-2 border-muted mb-4">
      <h3 className="font-medium text-lg mb-3">Meter Status</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Physical Status</label>
          <Badge
            variant="outline"
            className="w-full py-2 justify-center bg-green-50"
          >
            <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
            Installed
          </Badge>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Assignment Status</label>
          <Badge
            variant="outline"
            className="w-full py-2 justify-center bg-blue-50"
          >
            <UserCheck className="h-4 w-4 mr-2 text-blue-500" />
            {assignmentStatus ? "Assigned" : "Unassigned"}
          </Badge>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Reading Status</label>
          <Badge
            variant="outline"
            className="w-full py-2 justify-center bg-yellow-50"
          >
            <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" />
            Normal
          </Badge>
        </div>
      </div>
    </div>
  );

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-6 border-t">
      <button
        type="button"
        onClick={handleCancel}
        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={meterForm.handleSubmit(handleSubmitForm)}
        disabled={!isEditing && meterNoLoading}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {!isEditing && meterNoLoading
          ? "Generating..."
          : isEditing
          ? "Update Meter"
          : "Add Meter"}
      </button>
      {!isEditing && (
        <button
          type="button"
          onClick={meterForm.handleSubmit(handleNextSubmit)}
          disabled={meterNoLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {meterNoLoading ? "Generating..." : "Add & Next"}
        </button>
      )}
    </div>
  );

  return (
    <div className="page-container">
      <div className="w-full">
        {/* Header */}
        <div className="mb-4 flex items-center">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to meters
          </Button>
        </div>

        {/* Main Card */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center p-4">
              <div>
                <CardTitle className="page-subheading flex items-center gap-2">
                  {isEditing ? "Edit Meter" : "Add New Meter"}
                  <CustomTooltip message={`${!isEditing ? 'Fill out this form to register a new meter with all required details.' : 'Update the meter details. Ensure the information is accurate before saving.'}`} />
                </CardTitle>
                <CardDescription className="card-subheading-text">
                  {isEditing
                    ? "Update meter details"
                    : "Fill in the details to add a new meter"}
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-base text-neutral-700">
                <span>{`#${String(
                  isEditing
                    ? displayMeterNo
                    : meterNo?.meterNumber || "Generating..."
                )}`}</span>
              </Badge>
            </div>
          </CardHeader>

          <CardContent>
            {/* Meter Status Section */}
            {renderMeterStatusBadges()}

            {/* Dynamic Form */}
            <DynamicForm
              form={meterForm}
              config={formConfig}
              fields={fields}
              onSubmit={handleSubmitForm}
              gridColumns={4}
              onFieldChange={handleFieldChange}
              renderCustomFooter={renderCustomFooter}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddEditMeter;
